import React, { useState, useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider } from 'antd';
import enUS from 'antd/locale/en_US';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import { authService } from './services/apiService';
import { message } from 'antd';

// 认证令牌有效期（毫秒）
const AUTH_TOKEN_EXPIRY = 8 * 60 * 60 * 1000; // 8小时

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [authTokenExpiresAt, setAuthTokenExpiresAt] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // 检查认证令牌是否将要过期
  useEffect(() => {
    if (authTokenExpiresAt) {
      // 计算过期前的刷新时间点（例如，提前30分钟）
      const refreshThreshold = 30 * 60 * 1000; // 30分钟
      const timeToRefresh = authTokenExpiresAt - Date.now() - refreshThreshold;
      
      if (timeToRefresh > 0) {
        // 设置定时器在令牌接近过期时刷新
        const refreshTimer = setTimeout(() => {
          // 只在用户仍然登录的情况下刷新令牌
          if (isAuthenticated && currentUser) {
            refreshAuthToken();
          }
        }, timeToRefresh);
        
        // 清理定时器
        return () => clearTimeout(refreshTimer);
      }
    }
  }, [authTokenExpiresAt, isAuthenticated, currentUser]);

  // 刷新认证令牌
  const refreshAuthToken = async () => {
    if (!currentUser) return;
    
    try {
      // 设置新的过期时间
      const newExpiresAt = Date.now() + AUTH_TOKEN_EXPIRY;
      
      // 更新认证数据
      const authData = {
        username: currentUser,
        expiresAt: newExpiresAt
      };
      
      // 验证令牌
      const isValid = await authService.validateToken(authData);
      
      if (isValid) {
        // 更新状态和会话存储
        setAuthTokenExpiresAt(newExpiresAt);
        sessionStorage.setItem('authData', JSON.stringify(authData));
        console.log('Auth token refreshed successfully');
      } else {
        console.error('Token validation failed during refresh');
        handleLogout();
      }
    } catch (error) {
      console.error('Error refreshing auth token:', error);
      message.error('Session expired. Please login again.');
      handleLogout();
    }
  };

  // 检查用户是否已登录（组件挂载时）
  useEffect(() => {
    const checkAuthentication = async () => {
      setIsLoading(true);
      try {
        // 从 sessionStorage 检查认证数据
        const authDataStr = sessionStorage.getItem('authData');
        
        if (authDataStr) {
          const authData = JSON.parse(authDataStr);
          
          // 验证数据格式
          if (!authData || typeof authData !== 'object' || !authData.username || !authData.expiresAt) {
            console.error('Invalid auth data format');
            sessionStorage.removeItem('authData');
            setIsLoading(false);
            return;
          }
          
          // 检查认证是否过期
          if (authData.expiresAt && new Date().getTime() < authData.expiresAt) {
            // 验证令牌
            const isValid = await authService.validateToken(authData);
            
            if (isValid) {
              setCurrentUser(authData.username);
              setIsAuthenticated(true);
              setAuthTokenExpiresAt(authData.expiresAt);
            } else {
              // 令牌无效，清除数据
              console.log('Invalid auth token');
              sessionStorage.removeItem('authData');
            }
          } else {
            // 认证已过期，清除数据
            console.log('Auth token expired');
            sessionStorage.removeItem('authData');
          }
        }
      } catch (error) {
        console.error('Error checking authentication:', error);
        // 出错时安全地清除认证数据
        sessionStorage.removeItem('authData');
      } finally {
        setIsLoading(false);
      }
    };
    
    checkAuthentication();
  }, []);

  // 处理登录
  const handleLogin = async (username) => {
    try {
      // 验证用户名
      if (!username) {
        console.error('Invalid username');
        return;
      }
      
      // 调用登录API
      const response = await authService.login(username);
      
      if (response.success) {
        setCurrentUser(username);
        setIsAuthenticated(true);
        setAuthTokenExpiresAt(response.data.expiresAt);
        
        return true;
      } else {
        message.error(response.message || 'Login failed');
        return false;
      }
    } catch (error) {
      console.error('Login error:', error);
      message.error(error.message || 'Login failed due to an error');
      return false;
    }
  };
  
  // 处理注销
  const handleLogout = () => {
    try {
      authService.logout();
      setCurrentUser(null);
      setIsAuthenticated(false);
      setAuthTokenExpiresAt(null);
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  // 显示加载状态
  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        Loading...
      </div>
    );
  }

  return (
    <ConfigProvider locale={enUS}>
      <Routes>
        <Route 
          path="/login" 
          element={
            isAuthenticated ? 
              <Navigate to="/dashboard" replace /> : 
              <Login onLogin={handleLogin} />
          } 
        />
        <Route 
          path="/dashboard" 
          element={
            isAuthenticated ? 
              <Dashboard user={currentUser} onLogout={handleLogout} /> : 
              <Navigate to="/login" replace />
          } 
        />
        <Route 
          path="*" 
          element={<Navigate to={isAuthenticated ? "/dashboard" : "/login"} replace />} 
        />
      </Routes>
    </ConfigProvider>
  );
};

export default App;